import { subDays, format } from 'date-fns';

export const LEAD_STATUSES = ['open', 'in_progress', 'closed', 'expired'];

export const TEMPERATURE_BUCKETS = ['hot', 'warm', 'cold'];

export const TEMPERATURE_BUCKET_VALUES = {
  hot: [4, 5],
  warm: [3],
  cold: [1, 2],
};

export const EMPTY_FILTERS = {
  status: [],
  temperatureBuckets: [],
  assignedToMe: false,
  waitingForReply: false,
  isFavorite: false,
  createdAfter: '',
  createdBefore: '',
  collaborator: '', // novo campo visual
  department: '', // internal_name do departamento
  list: '', // uuid da lista do colaborador (chips abaixo da busca)
};

// Contorno provisório do bug de paginação de leads: em vez de uma data fixa,
// o filtro padrão pega sempre os últimos 5 dias (hoje - 5 dias).
export const getDefaultCreatedAfter = () => format(subDays(new Date(), 5), 'yyyy-MM-dd');

export const DEFAULT_FILTERS = {
  ...EMPTY_FILTERS,
  status: ['open', 'in_progress'],
  createdAfter: getDefaultCreatedAfter(),
  collaborator: '',
};

// Chips Favoritas/lista sobrescrevem os filtros, mas os de "Todas" ficam
// guardados em `allFilters` (persistido junto no sessionStorage) e voltam ao
// tocar em "Todas". Trocar de uma lista para outra mantém o mesmo snapshot.
const withoutChipSelection = ({ allFilters, ...f }) => ({ ...f, isFavorite: false, list: '' });

export const selectChipFilters = (filters, patch) => ({
  ...EMPTY_FILTERS,
  ...patch,
  allFilters: filters.allFilters ?? withoutChipSelection(filters),
});

export const restoreAllFilters = (filters) =>
  filters.allFilters ?? withoutChipSelection(filters);

export const LEADS_FILTERS_STORAGE_KEY = 'beworks:leads:filters';
export const LEADS_SORT_STORAGE_KEY = 'beworks:leads:sort';

export const LEADS_DETAILED_STORAGE_KEY = 'beworks:leads:detailed';

// Modo de exibição da lista: cards detalhados (padrão) ou compactos.
// Vai no localStorage, não no sessionStorage dos filtros/ordenação: é uma
// preferência de leitura do colaborador, que deve sobreviver a fechar o app.
export const DEFAULT_DETAILED = true;

export const readStoredDetailed = () => {
  if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_DETAILED;
  try {
    const raw = window.localStorage.getItem(LEADS_DETAILED_STORAGE_KEY);
    return raw === null ? DEFAULT_DETAILED : raw === 'true';
  } catch {
    return DEFAULT_DETAILED;
  }
};

export const writeStoredDetailed = (detailed) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LEADS_DETAILED_STORAGE_KEY, String(detailed));
  } catch {
  }
};

export const LEAD_SORT_OPTIONS = [
  { value: 'temperature_desc', field: 'latest_temperature', sort: 'desc', labelKey: 'leads.sort.byTemperature' },
  { value: 'recent_desc', field: 'last_message_at', sort: 'desc', labelKey: 'leads.sort.byRecent' },
];

export const DEFAULT_SORT = 'recent_desc';

export const readStoredSort = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return DEFAULT_SORT;
  try {
    const raw = window.sessionStorage.getItem(LEADS_SORT_STORAGE_KEY);
    if (!raw) return DEFAULT_SORT;
    return LEAD_SORT_OPTIONS.some((o) => o.value === raw) ? raw : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
};

export const writeStoredSort = (sortValue) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(LEADS_SORT_STORAGE_KEY, sortValue);
  } catch {
  }
};

export const buildSortModel = (sortValue) => {
  const option = LEAD_SORT_OPTIONS.find((o) => o.value === sortValue)
    || LEAD_SORT_OPTIONS.find((o) => o.value === DEFAULT_SORT);
  return [
    { field: 'pin_position', sort: 'asc' },
    { field: option.field, sort: option.sort },
  ];
};

export const readStoredFilters = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return DEFAULT_FILTERS;
  try {
    const raw = window.sessionStorage.getItem(LEADS_FILTERS_STORAGE_KEY);

    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_FILTERS;
    return { ...EMPTY_FILTERS, ...parsed };
  } catch {
    return DEFAULT_FILTERS;
  }
};

export const writeStoredFilters = (filters) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(LEADS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
  }
};

const toStartOfDayIso = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const toEndOfDayIso = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
};

// Conta cada chave de EMPTY_FILTERS que está preenchida: filtro novo entra na contagem sozinho.
// Lista e Favoritos escolhidos pelos chips abaixo da busca não são "filtros"
// do painel: o chip selecionado já mostra o que está aplicado, então não
// entram no badge do botão de filtro. `allFilters` só existe com um chip
// selecionado; sem ele, `isFavorite` veio do painel e conta normalmente.
export const countActiveFilters = (filters) => {
  if (!filters) return 0;
  const chipSelected = Boolean(filters.allFilters);
  return Object.keys(EMPTY_FILTERS).filter((filterName) => {
    if (filterName === 'list') return false;
    if (filterName === 'isFavorite' && chipSelected) return false;
    const selection = filters[filterName];
    return Array.isArray(selection) ? selection.length > 0 : Boolean(selection);
  }).length;
};

// 1 bucket → usa gte/lte/exact (consulta mais legível).
// 2+ buckets → usa __in com a lista explícita de valores.
const pushTemperatureBucketItems = (items, buckets) => {
  if (!Array.isArray(buckets) || buckets.length === 0) return;
  if (buckets.length === 1) {
    const [only] = buckets;
    if (only === 'cold') {
      items.push({ field: 'latest_temperature__lte', operator: '=', value: 2 });
    } else if (only === 'warm') {
      items.push({ field: 'latest_temperature', operator: '=', value: 3 });
    } else if (only === 'hot') {
      items.push({ field: 'latest_temperature__gte', operator: '=', value: 4 });
    }
    return;
  }
  const values = [];
  buckets.forEach((b) => {
    (TEMPERATURE_BUCKET_VALUES[b] || []).forEach((v) => values.push(v));
  });
  if (values.length === 0) return;
  items.push({ field: 'latest_temperature__in', operator: '=', value: values.join(',') });
};

export const buildFilterModel = (f) => {
  const items = [];
  if (!f) return { items };

  if (f.status?.length) {
    items.push({ field: 'status__in', operator: '=', value: f.status.join(',') });
  }
  pushTemperatureBucketItems(items, f.temperatureBuckets);
  if (f.assignedToMe) {
    items.push({ field: 'assigned_to_me', operator: '=', value: true });
  }
  if (f.isFavorite) {
    items.push({ field: 'is_favorite', operator: '=', value: true });
  }
  if (f.list) {
    items.push({ field: 'list', operator: '=', value: f.list });
  }
  if (f.waitingForReply) {
    items.push({ field: 'waiting_for_reply', operator: '=', value: true });
  }
  if (f.collaborator) {
    items.push({ field: 'participated_collaborator_id', operator: '=', value: f.collaborator });
  }
  if (f.department) {
    items.push({ field: 'latest_department', operator: '=', value: f.department });
  }
  const gte = toStartOfDayIso(f.createdAfter);
  if (gte) {
    items.push({ field: 'created_at__gte', operator: '=', value: gte });
  }
  const lt = toEndOfDayIso(f.createdBefore);
  if (lt) {
    items.push({ field: 'created_at__lt', operator: '=', value: lt });
  }
  return { items };
};
